import axios from 'axios';
import Summary from 'components/ui/Summary';
import Button from 'components/ui/Button';
import FormikCheckboxField from 'components/ui/CheckboxField';
import FormikFileInputField from 'components/ui/FileInputField';
import FormikInputField from 'components/ui/InputField';
import FormikSelectField from 'components/ui/SelectField';
import { useAppContext } from 'context/useAppContext';
import { Form, Formik } from 'formik';
import axiosInstance from 'utils/axiosInstance';
import { toMinorDenomination } from 'utils/helpers';
import { identityValidationSchema } from 'utils/validations';
import { ReactComponent as ErrorSvg } from '../../assets/error.svg';
import Icon from 'components/shared/Icon';
import { useCallback, useState, useRef } from 'react';
import TermsAndConditionsModal from 'components/ui/TermsAndConditionsModal';
import { ReactComponent as Spinner } from '../../assets/spinner.svg';

interface CeviantPayConfig {
  publicKey: string;
  mode: 'DEV' | 'PROD';
  amount: number;
  currency: string;
  customerId: string | number;
  customerEmail: string;
  customerPhone?: number;
  onSuccess: (response: any) => void;
  onClose?: () => void;
}

declare global {
  interface Window {
    ceviantPay: (config: CeviantPayConfig) => void;
  }
}

export interface IIdentityFormValues {
  bvn: string;
  nin: string;
  internationalPassport: File | null;
  paybackPeriod: string;
  termsAndCondition: boolean;
}

const tenureOptions = [
  { value: '2.5', label: '0-30 days → 2.5%' },
  { value: '7.5', label: '31-90 days → 7.5%' },
  { value: '15', label: '91-180 days → 15%' },
];

const KycForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const captureDoneRef = useRef(false);

  const {
    appData: { initialDepositAmount, transactionId, publicKey, mode },
    formData: {
      emailAddress,
      firstName,
      lastName,
      dateOfBirth,
      phoneNumber,
      ticketPrice,
      bookingRef,
      gender,
    },
    setError,
    clearError,
    error,
  } = useAppContext();

  const triggerSDK = () => {
    window.ceviantPay({
      publicKey: publicKey,
      mode: mode === 'TEST' ? 'DEV' : 'PROD',
      amount: toMinorDenomination(initialDepositAmount),
      customerPhone: Number(phoneNumber),
      customerEmail: emailAddress,
      customerId: `${firstName}_${phoneNumber}`,
      currency: 'NGN',
      onSuccess: async (response: string) => {
        try {
          await axiosInstance.post('/onlending/transactions/repay', {
            transactionId,
            repaymentAmount: toMinorDenomination(initialDepositAmount),
            reference: response,
          });

          window.parent.postMessage(
            {
              message: 'onLendingSuccess',
              payload: {
                transactionId,
                amountPaid: initialDepositAmount,
                paymentReference: response,
              },
            },
            '*',
          );
        } catch (error) {
          console.error('Error confirming repayment transaction');
        }
      },
    });
  };

  const handleSubmit = async (values: IIdentityFormValues) => {
    clearError();

    if (captureDoneRef.current) {
      triggerSDK();
      return;
    }

    const formData = new FormData();
    const uploadUrl =
      mode === 'TEST'
        ? 'https://app.uat.ceviant.co/api/upload'
        : 'https://app.ceviant.co/api/upload';

    formData.append('file', values.internationalPassport as Blob);

    try {
      setIsLoading(true);
      const upload = await axios.post(`${uploadUrl}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const payload = {
        transactionId,
        firstName,
        lastName,
        dateOfBirth,
        email: emailAddress,
        phoneNumber,
        gender,
        ticketPrice: toMinorDenomination(ticketPrice),
        bookingRef,
        bvn: values.bvn,
        nin: values.nin,
        passportDocumentId: upload.data,
        customerRate: Number(values.paybackPeriod),
      };

      await axiosInstance.post('/onlending/bookings/capture/data', payload);
      captureDoneRef.current = true;
      triggerSDK();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      let errorMessage =
        'An error occurred while submitting the form. Please try again.';
      if (axios.isAxiosError(error)) {
        errorMessage = (error.response?.data as any)?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    }
  };

  const handleCancel = useCallback(() => {
    window.parent.postMessage({ message: 'onLendingClose' }, '*');
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-12 px-4">
        <Icon
          svg={ErrorSvg}
          className="w-24 h-24 lg:w-32 text-red-600 mt-0.5 mr-3 flex-shrink-0"
        />
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex items-center justify-center">
            <p className="text-red-800 text-lg text-center">
              {error || 'An error occurred. Please try again.'}
            </p>
          </div>
        </div>
        <div className="flex gap-3 mt-24 md:mt-10 w-full max-w-md">
          <Button
            onClick={handleCancel}
            className="flex-1 !text-lg"
            variant="outline"
            aria-label="Cancel transaction"
          >
            Cancel
          </Button>
          <Button
            onClick={clearError}
            className="flex-1 !text-lg"
            variant="primary"
            aria-label="Retry transaction"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <section className="px-2 md:px-6 h-full overflow-y-auto pb-6 w-full">
      <h2 className="font-grotesk font-semibold text-xl">KYC Details</h2>
      {isLoading && (
        <div className="fixed top-0 left-0 inset-0 z-[1000] bg-gray-900 bg-opacity-30 h-full w-full flex flex-col items-center justify-center gap-4">
          <Spinner
            className="w-20 h-20 animate-spin"
            aria-label="Opening Payment Portal"
          />
          <p className="text-white/60 text-lg mt-4">Opening Payment Portal</p>
        </div>
      )}
      <Formik
        initialValues={{
          bvn: '',
          nin: '',
          internationalPassport: null,
          paybackPeriod: '',
          termsAndCondition: false,
        }}
        validationSchema={identityValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, isSubmitting, values }) => (
          <Form className="my-3 grid gap-4 w-full">
            <FormikInputField
              name="bvn"
              label="BVN"
              placeholder="Enter BVN"
            />
            <FormikInputField
              name="nin"
              label="NIN"
              placeholder="Enter NIN"
            />
            <FormikFileInputField
              name="internationalPassport"
              label="International Passport"
              accept=".pdf,.jpg,.jpeg,.png"
              maxSize={10}
              className="!font-grotesk"
            />
            <h2 className="font-grotesk font-semibold text-xl mt-6">
              Repayment details
            </h2>
            <div>
              <FormikSelectField
                name="paybackPeriod"
                label="Payback Period"
                options={tenureOptions}
                placeholder="Select Payback Period"
              />

              {values.paybackPeriod && <Summary />}
            </div>
            <div className="flex items-center justify-center">
              <FormikCheckboxField
                name="termsAndCondition"
                label={
                  <span className="text-base">
                    I agree to the{' '}
                    <span
                      onClick={() => setIsOpen(!isOpen)}
                      className="text-blue-600 underline underline-offset-2"
                    >
                      Terms
                    </span>{' '}
                    and repayment schedule.
                  </span>
                }
              />
            </div>
            <div className="w-full mt-8">
              <Button
                type="submit"
                className="w-full !font-grotesk !font-medium !text-lg md:!text-xl"
                disabled={isSubmitting || !isValid}
              >
                Proceed to Pay Deposit
              </Button>
            </div>

            {isOpen && <TermsAndConditionsModal setIsOpen={setIsOpen} />}
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default KycForm;
